/**
 * FilterToolbar — the one toolbar shape for a filterable list.
 *
 * Search on the bar, because it is the control staff reach for first;
 * everything else behind one Filters button, and shown as a clearable pill
 * only while it is set. The Awards and Balances pages took this shape on
 * 2026-09-14 (`GrantTotalsToolbar`), Applications the day before; the Users
 * page (roster and assignments) took it the same week, at which point the
 * shape was extracted here rather than copied a third time (Victor, #1980:
 * "replicate the toolbar (search, filter, etc.)" — replicate the pattern, not
 * the file).
 *
 * The toolbar owns the bar, the pill row and the dialog chrome. The CALLER
 * owns the filter state and passes the dialog's fields as children, so the
 * aria-labels a page's tests already read (`Country`, `Lifecycle`, `Role`…)
 * stay on the fields the page declares. Nothing here knows what a filter is.
 *
 * Slots, left to right on the bar: search · Filters (with a count) · `extras`
 * (a group-by checkbox) · `actions` (Export CSV, a primary) · `trailing` (a
 * lens note). Under the bar: `quickFilters` (unset shortcuts, e.g. "Pending
 * signups · 3"), the applied-search pill, the set-filter pills, Clear all.
 *
 * `variant="icons"` (Families roster, 2026-09-14 — Victor: "two icons to
 * handle everything") collapses the bar to two icon buttons: a magnifying
 * glass that opens a small search dialog, and the sliders that open the filter
 * dialog. The applied search still shows as a pill, so the row reads without
 * either dialog open. `(?)` roster-search-behind-icon records the alternative.
 *
 * On a coarse pointer the two icon buttons (36 px outline squares) reach 44 px
 * (D-2420, #2494) with `COARSE_HIT_36_OUTLINE`, each its own `isolate`
 * context. The row is `isolate` too and opens to `gap-3` on touch, so a hit
 * area never reaches a neighbour's drawn box, across or between wrapped lines.
 *
 * The labelled bar (#3001) reaches 44 px the same way: the 36 px outline
 * Filters button takes `COARSE_HIT_Y_36_OUTLINE` (5 px above and below; it is
 * already wider than 44 px), and the search box, which cannot carry a
 * `::after`, grows its own box to 44 px on a coarse pointer only. Desktop
 * draws both at 36 px as before.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { COARSE_HIT_36_OUTLINE, COARSE_HIT_Y_36_OUTLINE } from '@/lib/touch-target';
import { MonoLabel } from './MonoLabel';

/** The input class every field inside the dialog uses, so the grid reads as one form. */
export const TOOLBAR_FIELD_CLASS =
  'rounded border border-input bg-background px-2 py-1.5 text-body focus:outline-none focus:ring-2 focus:ring-ring';

const PILL_CLASS =
  'px-3 py-1 rounded-full border border-stevens-indigo bg-stevens-indigo/5 transition-colors inline-flex items-center gap-1';

const QUICK_CLASS =
  'px-3 py-1 rounded-full border border-stevens-rule bg-transparent transition-colors inline-flex items-center gap-1 hover:border-stevens-indigo/40';

export interface ToolbarPill {
  key: string;
  /** "Country: CA" — the dimension and the value, so the row reads without the dialog. */
  label: string;
  clear: () => void;
}

export interface ToolbarQuickFilter {
  key: string;
  label: string;
  onClick: () => void;
}

export interface FilterToolbarSearch {
  /** Accessible name of the box — "Search awards", "Search users". */
  label: string;
  /** The server's cap on the query, so the box stops there (#2717). */
  maxLength?: number;
  placeholder: string;
  /** The live input value. */
  value: string;
  onChange: (value: string) => void;
  /**
   * The value the list is actually filtered by (debounced, or read back from
   * the URL). When set, the pill row shows "Search: …" with a clear control.
   * Omit it and the pill follows `value`.
   */
  applied?: string;
}

export interface FilterToolbarProps {
  /** Names the dialog ("Filter awards") and the toolbar landmark ("Filter awards toolbar"). */
  title: string;
  /** One sentence under the dialog title — what the filters do to the list. */
  dialogDescription?: string;
  search: FilterToolbarSearch;
  /** The set filters, as pills. Their count is the badge on the Filters button. */
  pills: ToolbarPill[];
  /** Clears every filter at once; offered once two or more are set. */
  onClearAll: () => void;
  /** Unset shortcuts under the bar — pressing one sets a filter. */
  quickFilters?: ToolbarQuickFilter[];
  /**
   * Controls BEFORE the search — the noun the toolbar is about, when the list
   * is one of several (the form builder's form picker, 2026-09-16). Reads
   * left to right as "this thing · find in it · narrow it · act on it".
   */
  leading?: ReactNode;
  /** Controls after the Filters button (a group-by checkbox). */
  extras?: ReactNode;
  /** Buttons at the end of the bar (Export CSV, the page primary when it has no header slot). */
  actions?: ReactNode;
  /** Trailing note on the bar (the cohort-lens override). */
  trailing?: ReactNode;
  /** The dialog body — the filter fields, laid out on a two-column grid. */
  children: ReactNode;
  /**
   * `bar` (default): a search box on the bar and a labelled Filters button.
   * `icons`: a search icon that opens a search dialog, and a filter icon.
   */
  variant?: 'bar' | 'icons';
  className?: string;
}

export function FilterToolbar({
  title,
  dialogDescription,
  search,
  pills,
  onClearAll,
  quickFilters = [],
  leading,
  extras,
  actions,
  trailing,
  children,
  variant = 'bar',
  className,
}: FilterToolbarProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const filterCount = pills.length;
  const appliedSearch = search.applied ?? search.value;
  const icons = variant === 'icons';

  return (
    <div className={className ? `space-y-3 ${className}` : 'space-y-3'}>
      <div className="isolate flex flex-wrap items-center gap-2 [@media(pointer:coarse)]:gap-3" role="toolbar" aria-label={t('filterToolbar.toolbarLabel', { title })}>
        {leading}
        {icons ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={`h-9 w-9 p-0 ${COARSE_HIT_36_OUTLINE}`}
            onClick={() => setSearchOpen(true)}
            aria-label={search.label}
            title={search.label}
            aria-haspopup="dialog"
            aria-expanded={searchOpen}
          >
            <Search className="h-4 w-4" aria-hidden />
          </Button>
        ) : (
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stevens-ink-3"
              aria-hidden
            />
            <Input
              type="search"
              maxLength={search.maxLength}
              aria-label={search.label}
              placeholder={search.placeholder}
              className="pl-8 h-9 [@media(pointer:coarse)]:h-11"
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
            />
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={icons ? `h-9 w-9 p-0 ${COARSE_HIT_36_OUTLINE}` : COARSE_HIT_Y_36_OUTLINE}
          onClick={() => setOpen(true)}
          aria-label={icons ? t('filterToolbar.filters') : undefined}
          title={icons ? t('filterToolbar.filters') : undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <SlidersHorizontal className={icons ? 'h-4 w-4' : 'mr-2 h-3.5 w-3.5'} aria-hidden />
          {!icons && t('filterToolbar.filters')}
          {filterCount > 0 && (
            <span
              className={
                icons
                  ? 'absolute -right-1.5 -top-1.5 rounded-full bg-stevens-indigo-solid px-1.5 text-label leading-4 text-stevens-indigo-solid-fg'
                  : 'ml-2 rounded-full bg-stevens-indigo/10 px-1.5 text-caption text-stevens-indigo'
              }
            >
              {filterCount}
            </span>
          )}
        </Button>
        {extras}
        {actions}
        {trailing}
      </div>

      {icons && (
        <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{search.label}</DialogTitle>
              <DialogDescription>{t('filterToolbar.searchDialogDescription')}</DialogDescription>
            </DialogHeader>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stevens-ink-3"
                aria-hidden
              />
              <Input
                type="search"
              maxLength={search.maxLength}
                autoFocus
                aria-label={search.label}
                placeholder={search.placeholder}
                className="pl-8 h-9"
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setSearchOpen(false); }}
              />
            </div>
            <DialogFooter>
              {search.value && (
                <Button type="button" variant="ghost" size="sm" onClick={() => search.onChange('')}>{t('filterToolbar.clear')}</Button>
              )}
              <Button type="button" size="sm" onClick={() => setSearchOpen(false)}>{t('filterToolbar.done')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {(filterCount > 0 || appliedSearch || quickFilters.length > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {quickFilters.map((q) => (
            <button key={q.key} type="button" onClick={q.onClick} className={QUICK_CLASS}>
              <MonoLabel className="text-caption">{q.label}</MonoLabel>
            </button>
          ))}
          {appliedSearch && (
            <button type="button" onClick={() => search.onChange('')} className={PILL_CLASS}>
              <MonoLabel tone="indigo" className="text-caption">{t('filterToolbar.searchPill', { value: appliedSearch })}</MonoLabel>
              <X className="h-3 w-3 text-stevens-indigo" aria-hidden />
              <span className="sr-only">{t('filterToolbar.clearSearch')}</span>
            </button>
          )}
          {pills.map((p) => (
            <button key={p.key} type="button" onClick={p.clear} className={PILL_CLASS}>
              <MonoLabel tone="indigo" className="text-caption">{p.label}</MonoLabel>
              <X className="h-3 w-3 text-stevens-indigo" aria-hidden />
              <span className="sr-only">{t('filterToolbar.clearPill', { label: p.label })}</span>
            </button>
          ))}
          {filterCount >= 2 && (
            <button type="button" onClick={onClearAll} className="hover:underline">
              <MonoLabel className="text-caption font-normal">{t('filterToolbar.clearAll')}</MonoLabel>
            </button>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg flex flex-col max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {dialogDescription && <DialogDescription>{dialogDescription}</DialogDescription>}
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 overflow-y-auto flex-1 min-h-0 -mx-6 px-6 py-1">
            {children}
          </div>

          <DialogFooter>
            {filterCount > 0 && (
              <Button type="button" variant="ghost" size="sm" onClick={onClearAll}>{t('filterToolbar.clearAll')}</Button>
            )}
            <Button type="button" size="sm" onClick={() => setOpen(false)}>{t('filterToolbar.done')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
